import { useQuery } from "@tanstack/react-query";
import { Flame, Star, UtensilsCrossed, TrendingUp, TrendingDown, Minus, Users, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSessionLocation } from "@/hooks/useSessionLocation";

interface TrendingPanelProps {
  onSelect?: (restaurantName: string) => void;
}

interface TrendingDish {
  rank: number;
  name: string;
  trendDirection: "up" | "down" | "stable" | "new";
  orderCount: number;
  restaurantCount: number;
}

interface TrendingDishesResponse {
  dishes: TrendingDish[];
  period: string;
  generatedAt: string;
}

function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

function TrendIcon({ direction }: { direction: TrendingDish["trendDirection"] }) {
  if (direction === "up" || direction === "new") return <TrendingUp className="w-3 h-3 text-green-500 flex-shrink-0" />;
  if (direction === "down") return <TrendingDown className="w-3 h-3 text-red-400 flex-shrink-0" />;
  return <Minus className="w-3 h-3 text-muted-foreground flex-shrink-0" />;
}

function formatTimeAgo(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "Recently";
  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 0) return diffHours === 0 ? "Just now" : `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default function TrendingPanel({ onSelect }: TrendingPanelProps) {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const { customLatitude, customLongitude } = useSessionLocation();

  const hasLocation = Number.isFinite(customLatitude) && Number.isFinite(customLongitude);

  const trendingDishesParams = new URLSearchParams({ period: "7d", limit: "5" });
  if (hasLocation) {
    trendingDishesParams.set("lat", String(customLatitude));
    trendingDishesParams.set("lng", String(customLongitude));
    trendingDishesParams.set("radius_miles", "25");
  }

  const { data: trendingDishesData, isLoading: trendingDishesLoading } = useQuery<TrendingDishesResponse>({
    queryKey: ["/api/feed/trending-dishes", hasLocation ? customLatitude : null, hasLocation ? customLongitude : null],
    queryFn: async () => {
      const res = await fetch(`/api/feed/trending-dishes?${trendingDishesParams.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: topRatedData, isLoading: topRatedLoading } = useQuery<{
    restaurants: Array<{ id: string; name: string; cuisine: string; avgRating: number; reviewCount: number }>;
  }>({
    queryKey: ["/api/feed/top-rated-week"],
    queryFn: async () => {
      const res = await fetch("/api/feed/top-rated-week", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: newSpotsData, isLoading: newSpotsLoading } = useQuery<{
    restaurants: Array<{ id: string; name: string; cuisine: string }>;
  }>({
    queryKey: ["/api/feed/new-spots"],
    queryFn: async () => {
      const res = await fetch("/api/feed/new-spots", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: friendsData, isLoading: friendsLoading } = useQuery<{
    reviews: Array<{
      id: string;
      restaurantId: string;
      restaurantName: string;
      rating: number;
      createdAt: string;
      reviewerName: string;
      reviewerAvatar: string | null;
    }>;
  }>({
    queryKey: ["/api/feed/friends-recent"],
    queryFn: async () => {
      const res = await fetch("/api/feed/friends-recent", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    staleTime: 2 * 60 * 1000,
  });

  const trendingDishes = trendingDishesData?.dishes ?? [];
  const topRated = topRatedData?.restaurants ?? [];
  const newSpots = newSpotsData?.restaurants ?? [];
  const friendReviews = friendsData?.reviews ?? [];

  return (
    <div className="p-4 flex flex-col gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Flame className="w-4 h-4 text-orange-500" />
            Trending Now
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {trendingDishesLoading ? (
            <>
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-3/4" />
            </>
          ) : trendingDishes.length === 0 ? (
            <p className="text-xs text-muted-foreground">No trending dishes yet</p>
          ) : (
            trendingDishes.map((dish) => (
              <button
                key={dish.rank}
                className="flex items-center justify-between text-sm text-left hover-elevate active-elevate-2 rounded-md px-1 py-0.5 w-full"
                onClick={() => onSelect?.(dish.name)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-bold text-muted-foreground w-4 flex-shrink-0">{dish.rank}</span>
                  <span className="flex flex-col min-w-0">
                    <span className="font-medium truncate">{toTitleCase(dish.name)}</span>
                    <span className="text-xs text-muted-foreground">
                      {dish.orderCount} orders · {dish.restaurantCount} spot{dish.restaurantCount !== 1 ? "s" : ""}
                    </span>
                  </span>
                </div>
                <TrendIcon direction={dish.trendDirection} />
              </button>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Star className="w-4 h-4 text-yellow-500" />
            Top Rated This Week
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {topRatedLoading ? (
            <>
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-3/4" />
            </>
          ) : topRated.length === 0 ? (
            <p className="text-xs text-muted-foreground">No data yet this week</p>
          ) : (
            topRated.map((r) => (
              <button
                key={r.id}
                className="flex flex-col text-sm text-left hover-elevate active-elevate-2 rounded-md px-1 py-0.5"
                onClick={() => onSelect?.(r.name)}
              >
                <span className="font-medium">{r.name}</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  {r.cuisine && <span>{r.cuisine}</span>}
                  {r.cuisine && <span>·</span>}
                  <span className="text-yellow-600 dark:text-yellow-400">{r.avgRating.toFixed(1)}</span>
                  <span>·</span>
                  <span>{r.reviewCount} review{r.reviewCount !== 1 ? "s" : ""}</span>
                </span>
              </button>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <UtensilsCrossed className="w-4 h-4 text-primary" />
            New Spots
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {newSpotsLoading ? (
            <>
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-3/4" />
            </>
          ) : newSpots.length === 0 ? (
            <p className="text-xs text-muted-foreground">No new spots in the last 30 days</p>
          ) : (
            newSpots.map((r) => (
              <button
                key={r.id}
                className="flex flex-col text-sm text-left hover-elevate active-elevate-2 rounded-md px-1 py-0.5"
                onClick={() => onSelect?.(r.name)}
              >
                <span className="font-medium">{r.name}</span>
                {r.cuisine && (
                  <span className="text-xs text-muted-foreground">{r.cuisine}</span>
                )}
              </button>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Loved by People You Follow
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {friendsLoading ? (
            <>
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-3/4" />
            </>
          ) : friendReviews.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Follow people to see their picks.
            </p>
          ) : (
            friendReviews.map((r) => {
              const initials = r.reviewerName
                .split(" ")
                .map((w) => w[0])
                .slice(0, 2)
                .join("")
                .toUpperCase();
              const starsOut5 = (r.rating / 10) * 5;
              return (
                <button
                  key={r.id}
                  className="flex items-center gap-2 text-sm text-left hover-elevate active-elevate-2 rounded-md px-1 py-0.5 w-full"
                  onClick={() => onSelect?.(r.restaurantName)}
                >
                  {r.reviewerAvatar ? (
                    <img
                      src={r.reviewerAvatar}
                      alt={initials}
                      className="flex-shrink-0 w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-semibold">
                      {initials}
                    </span>
                  )}
                  <span className="flex flex-col min-w-0">
                    <span className="font-medium truncate">{r.restaurantName}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <span className="truncate">{r.reviewerName.split(" ")[0]}</span>
                      <span>·</span>
                      <span className="text-yellow-600 dark:text-yellow-400">{starsOut5.toFixed(1)}★</span>
                      <span>·</span>
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span>{formatTimeAgo(r.createdAt)}</span>
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
